<script setup>
import { t } from '../i18n';

// Bottom tab bar on a phone (docs/design/SPEC.md, «Нижнее меню», decision 24). Active item is white, not green (decision 2).
const props = defineProps({
  // 'home' | 'markets' | 'news' | 'settings'
  current: {
    type: String,
    required: true,
  },
  // Amber dot on «Профиль»: the API key was removed.
  profileAlert: {
    type: Boolean,
    default: false,
  },
  // A screen inside the active tab (operations, payouts, analytics inside «Портфель»): tapping the tab goes to its root.
  nested: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['navigate', 'soon']);

const ITEMS = [
  { view: 'home', label: 'nav.portfolio', icon: 'M4 8.5h16v10.5H4zM9 8.5V6a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 6v2.5M4 13h16' },
  // «Рынки» — the heatmap (docs/heatmap/SPEC.md, decision 3).
  { view: 'markets', label: 'nav.markets', icon: 'M4 19.5h16M6.5 16V11M10.5 16V7M14.5 16v-5.5M18.5 16V5' },
  { view: 'news', label: 'nav.news', icon: 'M4.5 5h12v14h-10a2 2 0 0 1-2-2zM16.5 9h3v8a2 2 0 0 1-2 2h-1M8 9h5M8 12.5h5M8 16h3' },
  { view: 'settings', label: 'nav.profile', icon: 'M12 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5zM5 20c.8-3.4 3.6-5.5 7-5.5s6.2 2.1 7 5.5' },
];

function go(item) {
  if (item.soon) {
    emit('soon', t(item.soon));
    return;
  }
  const view = item.view;
  // Tapping the active item scrolls the screen to the top, as in iOS; inside the tab it goes to the root.
  if (view === props.current && !props.nested) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  emit('navigate', view);
}
</script>

<template>
  <nav
    :aria-label="t('nav.label')"
    class="fixed inset-x-0 bottom-0 z-40 border-t border-slate-800/80 bg-slate-950/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md sm:hidden"
  >
    <div class="grid h-[52px] grid-cols-4">
      <button
        v-for="item in ITEMS"
        :key="item.view"
        type="button"
        class="relative flex flex-col items-center justify-center gap-1 text-xs font-medium leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
        :class="item.view === current ? 'text-slate-100' : item.soon ? 'text-slate-600' : 'text-slate-500'"
        :aria-current="item.view === current ? 'page' : undefined"
        :aria-disabled="item.soon ? 'true' : undefined"
        @click="go(item)"
      >
        <svg
          class="size-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path :d="item.icon" />
        </svg>
        <span>{{ t(item.label) }}</span>
        <span
          v-if="item.view === 'settings' && profileAlert"
          class="absolute right-[calc(50%-16px)] top-1.5 size-2 rounded-full bg-amber-400"
          aria-hidden="true"
        />
        <span
          v-if="item.view === 'settings' && profileAlert"
          class="sr-only"
        >{{ t('nav.profileAlert') }}</span>
      </button>
    </div>
  </nav>
</template>
